import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    // Check for existing user
    const savedUser = localStorage.getItem('dreamforge_user');
    const onboardingCompleted = localStorage.getItem('dreamforge_onboarding_completed');
    
    console.log('AuthProvider: Checking saved user:', savedUser);
    console.log('AuthProvider: Onboarding completed:', onboardingCompleted);
    
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        console.log('AuthProvider: Setting user:', userData);
        setUser(userData);
        
        // Only show onboarding for new users who haven't completed it AND haven't seen it before
        const hasSeenOnboarding = localStorage.getItem(`dreamforge_onboarding_seen_${userData.email}`);
        if (userData.isNewUser && !onboardingCompleted && !hasSeenOnboarding) {
          setShowOnboarding(true);
        }
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('dreamforge_user');
      }
    }
    
    setLoading(false);
  }, []);

  const login = (userData) => {
    console.log('AuthProvider: Login called with:', userData);
    
    // Create a stable user object
    const stableUserData = {
      ...userData,
      email: userData.email.toLowerCase().trim(),
      id: userData.email.toLowerCase().replace(/[^a-zA-Z0-9]/g, '_')
    };
    
    setUser(stableUserData);
    localStorage.setItem('dreamforge_user', JSON.stringify(stableUserData));
    
    // For new users, check if they've seen onboarding before
    const hasSeenOnboarding = localStorage.getItem(`dreamforge_onboarding_seen_${stableUserData.email}`);
    const onboardingCompleted = localStorage.getItem('dreamforge_onboarding_completed');
    
    if (stableUserData.isNewUser && !onboardingCompleted && !hasSeenOnboarding) {
      setShowOnboarding(true);
      // Mark that they've seen the onboarding (even if they skip it)
      localStorage.setItem(`dreamforge_onboarding_seen_${stableUserData.email}`, 'true');
    }
  };

  const logout = () => {
    console.log('AuthProvider: Logout called');
    setUser(null);
    setShowOnboarding(false);
    localStorage.removeItem('dreamforge_user');
    // Note: We keep the user's data and onboarding status for when they log back in
  };

  const completeOnboarding = () => {
    console.log('AuthProvider: Completing onboarding');
    setShowOnboarding(false);
    localStorage.setItem('dreamforge_onboarding_completed', 'true');
    
    // Update user to mark as no longer new
    if (user) {
      const updatedUser = { ...user, isNewUser: false };
      setUser(updatedUser);
      localStorage.setItem('dreamforge_user', JSON.stringify(updatedUser));
    }
  };

  const value = {
    user,
    loading,
    showOnboarding,
    login,
    logout,
    completeOnboarding,
    skipOnboarding: completeOnboarding
  };

  console.log('AuthProvider: Current state:', { user: !!user, loading, showOnboarding });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};